import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './AuditoriaNocautes.css';

/**
 * Página Admin - Auditoria de Nocautes
 * Exibe registro detalhado de nocautes por rodada.
 */
const AuditoriaNocautes = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [rodadas, setRodadas] = useState([]);
  const [rodadaSelecionada, setRodadaSelecionada] = useState('');
  const [nocautes, setNocautes] = useState([]);
  const [rodadaAtual, setRodadaAtual] = useState(null);

  const [loadingRodadas, setLoadingRodadas] = useState(true);
  const [loadingNocautes, setLoadingNocautes] = useState(false);
  const [error, setError] = useState('');

  const formatarData = (data) => {
    if (!data) return '-';

    return new Date(data).toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatarDataHora = (data) => {
    if (!data) return '-';

    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const carregarRodadas = useCallback(async () => {
    try {
      setLoadingRodadas(true);
      setError('');

      const response = await apiClient.get('/rodadas');

      const rodadasOrdenadas = (response.data || [])
        .sort((a, b) => new Date(b.data) - new Date(a.data));

      setRodadas(rodadasOrdenadas);

      if (rodadasOrdenadas.length > 0) {
        setRodadaSelecionada(rodadasOrdenadas[0].id);
        setRodadaAtual(rodadasOrdenadas[0]);
      }
    } catch (err) {
      console.error('Erro ao carregar rodadas:', err);
      setError(err.response?.data?.error || 'Erro ao carregar rodadas');
    } finally {
      setLoadingRodadas(false);
    }
  }, []);

  const carregarNocautes = useCallback(async (rodadaId) => {
    if (!rodadaId) {
      setNocautes([]);
      setRodadaAtual(null);
      return;
    }

    try {
      setLoadingNocautes(true);
      setError('');

      const response = await apiClient.get('/nocautes/admin/auditoria', {
        params: {
          rodada_id: rodadaId,
        },
      });

      setNocautes(response.data || []);

      const rodadaEncontrada = rodadas.find(r => String(r.id) === String(rodadaId));
      setRodadaAtual(rodadaEncontrada || null);
    } catch (err) {
      console.error('Erro ao carregar auditoria de nocautes:', err);
      setError(err.response?.data?.error || 'Erro ao carregar auditoria de nocautes');
      setNocautes([]);
    } finally {
      setLoadingNocautes(false);
    }
  }, [rodadas]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }

    carregarRodadas();
  }, [isAdmin, navigate, carregarRodadas]);

  useEffect(() => {
    if (rodadaSelecionada) {
      carregarNocautes(rodadaSelecionada);
    }
  }, [rodadaSelecionada, carregarNocautes]);

  const handleSelecionarRodada = (e) => {
    setRodadaSelecionada(e.target.value);
  };

  if (loadingRodadas) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container auditoria-nocautes-container">
      <div className="page-header auditoria-header">
        <div>
          <h1>🥊 Auditoria de Nocautes</h1>
          <p>Consulte todos os registros de nocaute por rodada.</p>
        </div>

        <button
          onClick={() => navigate('/admin/menu')}
          className="btn btn-secondary"
        >
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="auditoria-card">
        <label htmlFor="rodada-select">
          <strong>Selecione a rodada:</strong>
        </label>

        <select
          id="rodada-select"
          value={rodadaSelecionada}
          onChange={handleSelecionarRodada}
          className="form-control"
        >
          <option value="">Selecione uma rodada...</option>
          {rodadas.map((rodada) => (
            <option key={rodada.id} value={rodada.id}>
              {formatarData(rodada.data)} — {rodada.status}
            </option>
          ))}
        </select>
      </div>

      {rodadaAtual && (
        <div className="rodada-info-card">
          <h2>Rodada selecionada</h2>
          <div className="info-grid">
            <div>
              <strong>Data:</strong> {formatarData(rodadaAtual.data)}
            </div>
            <div>
              <strong>Status:</strong>{' '}
              <span className={`status-badge ${rodadaAtual.status}`}>
                {rodadaAtual.status === 'aberta' ? '🟢 Aberta' : '🔴 Fechada'}
              </span>
            </div>
            <div>
              <strong>Total de registros:</strong> {nocautes.length}
            </div>
            <div>
              <strong>Eliminações:</strong>{' '}
              {nocautes.filter(n => n.eliminou).length}
            </div>
          </div>
        </div>
      )}

      <div className="auditoria-lista-card">
        <h2>Registros da Rodada</h2>

        {loadingNocautes ? (
          <p>Carregando registros...</p>
        ) : !rodadaSelecionada ? (
          <p className="no-data">Selecione uma rodada para visualizar os registros.</p>
        ) : nocautes.length === 0 ? (
          <p className="no-data">Nenhum nocaute registrado nesta rodada.</p>
        ) : (
          <div className="auditoria-lista">
            {nocautes.map((nocaute, index) => (
              <div
                key={nocaute.id}
                className={`auditoria-item ${nocaute.eliminou ? 'eliminacao' : ''}`}
              >
                <div className="auditoria-numero">
                  {index + 1}
                </div>

                <div className="auditoria-conteudo">
                  <div className="auditoria-descricao">
                    {nocaute.descricao}
                  </div>

                  <div className="auditoria-meta">
                    Registrado em {formatarDataHora(nocaute.createdAt)}
                  </div>
                </div>

                <div className="auditoria-tipo">
                  {nocaute.eliminou ? 'Eliminação' : 'Nocaute'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaNocautes;